import localforage, {setDriver} from 'localforage';
import LogRocket from 'logrocket';
import axios from 'axios';

import {BlipChat} from 'blip-chat-widget';

import router from '../../router';

/**
 * Session Store
 * @namespace store.session
 * @memberof store
 */

let blipClient = new BlipChat();

export default {

  namespaced: true,
  /**
   * @namespace state
   * @memberof store.session
   */
  state: {

    blipSdkUAccount: null,
    /**
     * Session Token
     * @memberof store.session.state
     * @type Token
     */
    token: null,
    /**
     * Session Accounts
     * @memberof store.session.state
     * @type Array<Account>
     */
    userAccounts: [],
    /**
     * Session Selected Account
     * @memberof store.session.state
     * @type Account
     */
    currentAccount: null,
    /**
     * Session User
     * @memberof store.session.state
     * @type User
     */
    currentUser: null,
    featuresAccounts: null,
    /**
     * These functions will be triggered on account change
     * @memberof store.session.state
     * @type Array<Function>
     */
    accountChangeListeners: [],
    isInactive: false,
  },
  /**
   * @namespace getters
   * @memberof store.session
   */
  getters: {
    /**
     * Check if session token is expired
     *
     * @returns {Boolean} true if it's expired
     */
    isTokenExpired(state) {
      const {token} = state;
      if (token && token.expiresIn && token.createdAt) {
        const {expiresIn, createdAt} = token;
        const expireDate = new Date(createdAt.valueOf());
        expireDate.setMilliseconds(
          expireDate.getMilliseconds() + expiresIn * 1000,
        );
        return expireDate <= new Date();
      }
      return true;
    },
    /**
     * Check if session currentAccount is up to date
     *
     * @returns {Boolean} true if it's valid
     */
    isAccountValid(state) {
      return (givenAccounts) => {
        const {currentAccount} = state;

        if (!currentAccount) return false;
        return givenAccounts.some(
          acc => JSON.stringify(acc) === JSON.stringify(currentAccount),
        );
      };
    },
    /**
     * Check if user is logged in (has a valid session)
     *
     * @returns {Boolean} true if it's logged in and token is not expired
     */
    loggedIn(state, getters) {
      return state.token && !getters.isTokenExpired;
    },
    /**
     * Check if user is logged in (has a valid session)
     *
     * @returns {Array<Account>} list of current user accounts
     */
    accounts(st) {
      return st.userAccounts;
    },
    guestFeatures({currentAccount, currentUser}) {
      return (guestId = currentUser.id) => {
        if (currentUser && currentAccount && currentAccount.guests) {
          const guest = currentAccount
            .guests
            .find(g => g.id === guestId);
          return guest ? guest.features : [];
        }
        return [];
      };
    },
    isInactive(st) {
      return st.isInactive;
    }

  },
  /**
   * @namespace mutations
   * @memberof store.session
   */
  mutations: {
    setBlipSdkUAccount(state, blipSdkUAccount) {
      state.blipSdkUAccount = blipSdkUAccount;
    },
    /**
     * Set session token
     *
     * @param {Token} token
     */
    setToken(state, token) {
      state.token = token;
    },
    /**
     * Set session accounts
     *
     * @param {Array<Account>} accounts
     */
    setUserAccounts(st, accounts) {
      st.userAccounts = accounts;
    },
    /**
     * Set session selected account
     *
     * @param {Account} account
     */
    setCurrentAccount(st, currentAccount) {
      st.currentAccount = currentAccount;

      // Trigger account listeners
      st.accountChangeListeners.forEach(
        listener => listener(),
      );
    },
    /**
     * Set session current user
     *
     * @param {User} currentUser
     */
    setCurrentUser(st, currentUser) {
      st.currentUser = currentUser;
    },
    setFeaturesAccount(st, featuresAccounts) {
      st.featuresAccounts = featuresAccounts;
    },
    /**
     * Add account change listener
     *
     * @param {Function} listener will be called when current account changes
     */
    addAccountChangeListener(st, listener) {
      st.accountChangeListeners.push(listener);
    },
    /**
     * Remove account change listener
     *
     * @param {Function} listener the listener to remove
     */
    removeAccountChangeListener(st, listener) {
      st.accountChangeListeners = st.accountChangeListeners.filter(
        item => item !== listener,
      );
    },
    clearAccountChangeListeners(st, bool) {
      st.accountChangeListeners = [];
    },
    setIsInactive(st, bool) {
      st.isInactive = bool;
    }


  },
  /**
   * @namespace actions
   * @memberof store.session
   */
  actions: {
    /**
     * Creates a new session for a given token
     *
     * @memberof store.session.actions
     * @param {Object} payload
     * @param {Token} payload.token
     */
    async create({commit}, {token}) {
      await localforage.setItem('sessionToken', token);
      commit('setToken', token);
    },
    /**
     * load token and account from localStorage or indexDB (depends on browser)
     * if it exists
     *
     * @memberof store.session.actions
     */
    async loadFromStorage({commit}) {
      const token = await localforage.getItem('sessionToken');
      if (token) {
        commit('setToken', token);
      }
      const account = await localforage.getItem('sessionAccount');
      if (account) {
        commit('setCurrentAccount', account);
      }
    },
    /**
     * Removes current active session
     *
     * @memberof store.session.actions
     */
    async remove({commit}) {
      blipClient.destroy();
      blipClient = undefined;
      await localforage.removeItem('blipSdkUAccount');
      commit('setBlipSdkUAccount', null);
      // clear listeners to not trigger events on leaving
      commit('clearAccountChangeListeners');

      // clear session storage
      await localforage.removeItem('sessionToken');
      commit('setToken', null);
      await localforage.removeItem('sessionAccount');
      commit('setCurrentAccount', null);
    },
    /**
     * Load current active user
     *
     * @memberof store.session.actions
     */
    async loadUser({dispatch, commit, state}) {
      const {token} = state;
      const currentUser = await dispatch('users/get', token.userId, {root: true});
      if (process.env.VUE_APP_LOGROCKET) {
        LogRocket.identify(currentUser.id, {
          name: currentUser.name,
          email: currentUser.email,
          // TODO: add managerId to LogRocket
          // managerId: currentAccount.userOnwerId
        });
      }
      commit('setCurrentUser', currentUser);
    },
    /**
     * Load current active user accounts
     *
     * @memberof store.session.actions
     */
    async loadUserAccounts({dispatch, rootState, commit, getters: gt}) {
      const user = rootState.session.currentUser;

      blipClient
        .withAppKey('b3JzZWd1cHNwcm9kOjQyOWQzMTZlLTRmOGQtNDgyZS05NDU5LTE1NTA1NjhjYWE3Mw==')
        .withCustomCommonUrl('https://orsegups.chat.blip.ai/')
        .withButton({"color":"#052c8b","icon":""})
        .withCustomMessageMetadata({
          "cpf": user.cpfCnpj
        })
        .withoutHistory()
        .build();
      const accessToken = rootState.session.token.accessToken;
      let featuresAccounts = rootState.session.featuresAccounts;
      // get manager accounts or guest accounts
      const mineAccounts = await dispatch('accounts/find', {query: {userOwnerId: user.id}}, {root: true});
      const otherAccounts = await dispatch('accounts/find', {query: {guestId: user.id}}, {root: true});


      if (!featuresAccounts || featuresAccounts.length == 0) {
        let axiosConfig = {
          headers: {
            'Content-Type': 'application/json;charset=UTF-8',
            "Access-Control-Allow-Origin": "*",
            "Authorization": "Bearer " + accessToken
          }
        };
        const orsegupsIdUrl = process.env.VUE_APP_ORSEGUPSID_URI;
        let response = await axios.get(orsegupsIdUrl + '/account/all?product=2', axiosConfig);
        featuresAccounts = response.data;
        commit('setFeaturesAccount', featuresAccounts);
      }

      const accounts = [...mineAccounts, ...otherAccounts];
      if (accounts.length < 1) {
        throw Error('Não foi possível encontrar contas válidas para o usuário informado');
      }

      if (!gt.isInactive) {
        const activeAccounts = featuresAccounts.data.filter(a => !a.isActive);
        if (activeAccounts.length === featuresAccounts.data.length) {
          featuresAccounts.data.forEach((e) => e.features = [{id: 1, name: "BOLETOS_NOTAS"}, {
            id: 3,
            name: "GERIR_DOCUMENTOS"
          }]);
          commit('setFeaturesAccount', featuresAccounts);
          commit('setIsInactive', true);
          this.$notify("Você não possui nenhum contrato ativo. Restringimos o seu acesso apenas a Boletos e Documentos");
          router.push({name: 'Documents'});
        }
      }

      accounts.sort((a, b) => (b.isActive > a.isActive) ? 1 : -1);
      commit('setUserAccounts', accounts);
      // if there is no a local storage account, or is outdated
      if (!gt.isAccountValid(accounts)) {
        await dispatch('updateCurrentAccount', accounts[0]);
      }
    },
    /**
     * Update store and session account
     *
     * @memberof store.session.actions
     */
    async updateCurrentAccount({commit}, account) {
      await localforage.setItem('sessionAccount', account);
      commit('setCurrentAccount', account);
    },

  },
};
